import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  resolveContractorReferralCode,
  storeContractorReferralFallback,
  trackContractorReferralVisit,
} from "../../../infrastructure/utils/contractorReferralTracking";
import SolarentSiteFooter from "../../components/marketing/SolarentSiteFooter";

const TEAL_DEEP = "#044F79";

function buildContractorAuthPath(pathname, search = "", referralCode = "") {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete("ref");
  // Keep contractor referral in the URL so signup/login never depends only on localStorage.
  const code = (referralCode || "").trim();
  if (code) {
    params.set("contractorRef", code);
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

const ContractorReferralLandingPage = () => {
  const { referralCode: routeReferralCode } = useParams();
  const location = useLocation();
  const [tracking, setTracking] = useState(true);
  const [trackError, setTrackError] = useState(null);

  const referralCode = useMemo(
    () =>
      resolveContractorReferralCode({
        routeCode: routeReferralCode,
        search: location.search,
      }),
    [routeReferralCode, location.search]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!referralCode) {
        setTracking(false);
        setTrackError("کد معرف پیمانکار معتبر نیست.");
        return;
      }

      storeContractorReferralFallback(referralCode);
      await trackContractorReferralVisit({
        referralCode,
        landingUrl: window.location.href,
        force: false,
      });

      if (!cancelled) setTracking(false);
    };

    run().catch(() => {
      if (!cancelled) {
        setTracking(false);
        setTrackError("ثبت بازدید انجام نشد؛ می‌توانید ادامه دهید.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [referralCode, location.pathname, location.search]);

  const loginTo = buildContractorAuthPath("/login", location.search, referralCode);
  const signupTo = buildContractorAuthPath("/register/form", location.search, referralCode);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F6F8FB", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${TEAL_DEEP} 0%, #0A7EA4 100%)`,
          color: "#fff",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={2} alignItems="center" textAlign="center">
            <SolarPowerOutlinedIcon sx={{ fontSize: 56, opacity: 0.95 }} />
            <Typography variant="h4" fontWeight={800}>
              درخواست نیروگاه خورشیدی با معرفی پیمانکار
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 560, opacity: 0.92 }}>
              با ثبت‌نام از این لینک، پیمانکار معرف شما در اولویت اجرای پروژه شما قرار می‌گیرد و
              کارشناسان سولارنت نام شرکت پیمانکار را در مراحل بررسی مشاهده می‌کنند.
            </Typography>
            {tracking ? (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                در حال آماده‌سازی...
              </Typography>
            ) : null}
            {trackError ? (
              <Alert severity="warning" sx={{ width: "100%", maxWidth: 480 }}>
                {trackError}
              </Alert>
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 2 }}>
              <Button
                component={Link}
                to={signupTo}
                variant="contained"
                size="large"
                sx={{ bgcolor: "#F6AE2D", color: "#1a1a1a", fontWeight: 700, px: 4 }}
              >
                ثبت‌نام و شروع درخواست
              </Button>
              <Button
                component={Link}
                to={loginTo}
                variant="outlined"
                size="large"
                sx={{ borderColor: "#fff", color: "#fff", px: 4 }}
              >
                ورود
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <ShareOutlinedIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                مزایای ورود از لینک پیمانکار
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>اولویت در انتخاب پیمانکار اجرا</li>
              <li>ثبت پیمانکار پیشنهادی سولارنت روی درخواست شما</li>
              <li>پیگیری یکپارچه توسط تیم سولارنت</li>
            </Typography>
          </CardContent>
        </Card>
      </Container>

      <SolarentSiteFooter />
    </Box>
  );
};

export default ContractorReferralLandingPage;
